import { activePortalRegion } from '../config/portal-regions';
import { LegalAcceptanceGate } from './LegalDocuments';
import { sharedSecretReadPermissions } from '../utils/secret-sharing';
import toast from 'react-hot-toast';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  Settings, FileText, Key, LogOut,
  X, Globe, Webhook, Crown, Server,
  LinkIcon, Users,
  Send, Menu,
  Moon, Sun,
  Mails, BookOpen,
  TableProperties,
  Home,
  BarChart3,
  HelpCircle,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ShieldX,
  Workflow,
  Map,
  ScrollText,
  CirclePile,
  List,
  Contact,
  LucideIcon,
  LifeBuoy,
  Lock,
  Inbox,
  Bug,
  Siren,
  MessageCircle,
  MessageSquare,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAiAssistant } from '../contexts/AiAssistantContext';
const AiTemplateCreator = lazy(() => import('./AiTemplateCreator'));
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useUserSettings } from '../hooks/useUserSettings';
import PaymentFailureBanner from './PaymentFailureBanner';
import MaintenanceBanner from './MaintenanceBanner';
import UserPortalBanners from './UserPortalBanners';
import CommunicationEmailPrompt from './CommunicationEmailPrompt';
import ContactsOnboardingTour from './ContactsOnboardingTour';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { prefetchAnalyticsLanding } from '../hooks/useAnalytics';
import { usePermissions } from '../hooks/usePermissions';
import { useEmailUseCasePreference } from '../hooks/useEmailUseCasePreference';
import { apiService } from '../services/api';
import { type FeatureBadgeId } from '../utils/featureBadges';
import { useFeatureBadges } from '../hooks/useFeatureBadges';
const CreateWorkspaceModal = lazy(() => import('./CreateWorkspaceModal').then(m => ({ default: m.CreateWorkspaceModal })));
import ModalPortal from './ModalPortal';
import { PortalIcon } from './PortalIcon';
const BugReportPopup = lazy(() => import('./BugReportPopup'));

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.872-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}


const getBadgeClasses = (type?: string) => {
  switch (type) {
    case 'info':
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300';
    case 'warning':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
    default:
      return 'bg-slate-100 text-slate-800 dark:bg-zinc-800 dark:text-zinc-300';
  }
};

const getDotBadgeClasses = (type?: string) => {
  switch (type) {
    case 'info':
      return 'bg-cyan-600 dark:bg-cyan-400';
    case 'warning':
      return 'bg-amber-600 dark:bg-amber-400';
    default:
      return 'bg-slate-600 dark:bg-zinc-400';
  }
};

const env = import.meta.env.VITE_APP_ENV || 'production';
const portalRegionFlags: Record<string, string> = { us: '🇺🇸', au: '🇦🇺', ca: '🇨🇦', eu: '🇪🇺' };

// Pages accessible when account is suspended
const SUSPENDED_ALLOWED_PAGES = ['/', '/dashboard', '/analytics', '/docs', '/donate'];

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  suspendedAccess: boolean;
  badge?: string;
  badgeType?: string;
  badge2?: string;
  badge2Type?: string;
  featureBadgeId?: FeatureBadgeId;
  children?: Array<{
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: string;
    badgeType?: string;
    featureBadgeId?: FeatureBadgeId;
  }>;
  divider?: boolean;
  ownerOnly?: boolean;
}

function SuppressionNavigationIcon({ className }: { className?: string }) {
  return <PortalIcon name="suppressed" className={className} />;
}

function VirtualInboxNavigationIcon({ className }: { className?: string }) {
  return <PortalIcon name="inbox" className={className} />;
}

function SharedCredentialNavigationIcon({ className }: { className?: string }) {
  return <PortalIcon name="shared-credential" className={className} />;
}

const navigation: NavigationItem[] = [
  { name: 'Home', href: '/', icon: Home, suspendedAccess: true },
  { name: 'Domains', href: '/domains', icon: Globe, suspendedAccess: false },
  {
    name: 'Secrets',
    href: '/api-keys',
    icon: Lock,
    suspendedAccess: false,
    badge: 'New',
    badgeType: 'info',
    featureBadgeId: 'secrets',
    children: [
      { name: 'API Keys', href: '/api-keys', icon: Key },
      { name: 'SMTP', href: '/smtp', icon: Server },
      { name: 'Shared secrets', href: '/shared-secrets', icon: SharedCredentialNavigationIcon, badge: 'New', badgeType: 'info', featureBadgeId: 'sharedSecrets' },
    ],
  },
  { name: 'Mailbox', href: '/mailbox', icon: Inbox, suspendedAccess: false, badge: 'New', badgeType: 'info', featureBadgeId: 'mailbox' },
  { name: 'Virtual Inbox', href: '/virtual-inbox', icon: VirtualInboxNavigationIcon, suspendedAccess: false },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, suspendedAccess: true },
  { name: 'Templates', href: '/templates', icon: FileText, suspendedAccess: false },
  {
    name: 'Contacts',
    href: '/contacts',
    icon: Users,
    suspendedAccess: false,
    badge: 'New',
    badgeType: 'info',
    featureBadgeId: 'contacts',
    children: [
      { name: 'Directory', href: '/contacts', icon: Contact },
      { name: 'Custom Fields', href: '/contacts/custom-fields', icon: TableProperties },
      { name: 'Subscription Groups', href: '/contacts/subscription-groups', icon: List },
      { name: 'Segments', href: '/contacts/segments', icon: CirclePile },
    ],
  },
  { name: 'Suppression', href: '/contacts/suppressions', icon: SuppressionNavigationIcon, suspendedAccess: false },
  { name: 'Campaigns', href: '/campaigns', icon: Mails, suspendedAccess: false },
  { name: 'Workflows', href: '/workflows', icon: Workflow, suspendedAccess: false, badge: 'Beta', badgeType: 'info' },
  { name: 'Webhooks', href: '/webhooks', icon: Webhook, suspendedAccess: false },
  { name: 'Integrations', href: '/integrations', icon: LinkIcon, suspendedAccess: false },
  { name: 'Pricing', href: '/pricing', icon: Crown, suspendedAccess: false, ownerOnly: true },
  { name: 'Account', href: '/account', icon: Settings, suspendedAccess: false },
];

const NAV_PERMISSIONS: Record<string, string | string[]> = {
  '/domains': 'domain:read',
  '/api-keys': ['transactional_api_key:read', 'marketing_api_key:read', 'web_api_key:read'],
  '/smtp': 'smtp:read',
  '/shared-secrets': sharedSecretReadPermissions,
  '/mailbox': ['inbox:read', 'email:send'],
  '/virtual-inbox': 'email_event:read',
  '/analytics': 'analytics:read',
  '/templates': 'email_template:read',
  '/contacts': 'contact:read',
  '/contacts/custom-fields': 'contact:read',
  '/contacts/subscription-groups': 'subscription_group:read',
  '/contacts/segments': 'segment:read',
  '/contacts/suppressions': 'suppression:read',
  '/campaigns': 'campaign:read',
  '/workflows': 'workflow:read',
  '/webhooks': 'webhook:read',
  '/integrations': 'integration:read',
  '/pricing': 'billing:read',
};

const TRANSACTIONAL_NAVIGATION = new Set(['/', '/domains', '/api-keys', '/mailbox', '/virtual-inbox', '/analytics', '/templates', '/contacts/suppressions', '/webhooks', '/integrations', '/pricing', '/account']);

const infoMenuItems: Array<{
  name: string;
  href: string;
  icon: LucideIcon;
  badge?: string;
  badgeType?: string;
  featureBadgeId?: FeatureBadgeId;
}> = [
    { name: 'Documentation', href: 'https://www.simplysend.email/docs', icon: BookOpen },
    { name: 'Roadmap', href: 'https://productmapy.com/simply-send', icon: Map, badge: 'New', badgeType: 'info', featureBadgeId: 'roadmap' },
    { name: 'Release Notes', href: 'https://www.simplysend.email/release-notes', icon: ScrollText, badge: 'New', badgeType: 'info', featureBadgeId: 'releaseNotes' },
    { name: 'Contact Us', href: 'https://www.simplysend.email/contact', icon: MessageSquare },
  ];


export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout, switchWorkspace } = useAuth();
  const { preference: emailUseCasePreference } = useEmailUseCasePreference(user?.id);
  const { data: userSettingsData } = useUserSettings();
  const { hasPermission } = usePermissions();
  const canGenerateAiTemplates = hasPermission('email_template_ai:generate') && hasPermission('email_template:write');
  const { theme, setTheme } = useTheme();
  const { isOpen, isMinimized, closeAssistant, setIsMinimized, onTemplateCreated } = useAiAssistant();
  const [isMobile, setIsMobile] = useState(false);
  const [reauthRequired, setReauthRequired] = useState(false);

  useEffect(() => {
    const handleReauthRequired = () => setReauthRequired(true);
    window.addEventListener('simplysend:reauth-required', handleReauthRequired);
    return () => window.removeEventListener('simplysend:reauth-required', handleReauthRequired);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [contactsOpen, setContactsOpen] = useState(() => {
    const childPaths = ['/contacts', '/contacts/custom-fields', '/contacts/subscription-groups', '/contacts/segments'];
    return childPaths.some(p => location.pathname === p || (p !== '/contacts' && location.pathname.startsWith(p + '/')));
  });
  const [secretsOpen, setSecretsOpen] = useState(() => {
    const childPaths = ['/api-keys', '/smtp', '/shared-secrets'];
    return childPaths.some(p => location.pathname === p || (p !== '/contacts' && location.pathname.startsWith(p + '/')));
  });
  const { dismissBadge, shouldShowNavNewBadge } = useFeatureBadges();

  const visibleInfoMenuItems = infoMenuItems.map((item) => (
    item.badge === 'New' && !shouldShowNavNewBadge(item)
      ? { ...item, badge: undefined, badgeType: undefined }
      : item
  ));

  const [activeHoverMenu, setActiveHoverMenu] = useState<{
    name: string;
    top: number;
    left: number;
    children: Array<{
      name: string;
      href: string;
      icon: React.ComponentType<{ className?: string }>;
      badge?: string;
      badgeType?: string;
      featureBadgeId?: FeatureBadgeId;
    }>;
  } | null>(null);

  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname);
    const childPaths = ['/contacts', '/contacts/custom-fields', '/contacts/subscription-groups', '/contacts/segments'];
    if (childPaths.some(p => location.pathname === p || (p !== '/contacts' && location.pathname.startsWith(p + '/')))) {
      setContactsOpen(true);
    }
    const secretPaths = ['/api-keys', '/smtp', '/shared-secrets'];
    if (secretPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
      setSecretsOpen(true);
    }
    if (activeHoverMenu !== null) {
      setActiveHoverMenu(null);
    }
  }

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('desktopSidebarCollapsed');
      return saved ? JSON.parse(saved) === true : false;
    } catch {
      return false;
    }
  });
  const isSidebarCollapsed = desktopSidebarCollapsed && !isMobile;

  const toggleDesktopSidebar = () => {
    setDesktopSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('desktopSidebarCollapsed', JSON.stringify(next));
      } catch (e) {
        console.error('Failed to save sidebar state to localStorage', e);
      }
      return next;
    });
  };


  const [showRestrictedView, setShowRestrictedView] = useState(false);
  const [showPauseDetails, setShowPauseDetails] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [dismissedPaymentBanner, setDismissedPaymentBanner] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [showSupportPage, setShowSupportPage] = useState(false);
  const [showDiscordMenu, setShowDiscordMenu] = useState(false);
  const [showInfoMenu, setShowInfoMenu] = useState(false);

  // Update current time every second for pending status checks
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Expand parent menus when the onboarding tour highlights a collapsed child item
  useEffect(() => {
    const handleExpandMenu = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.menu === 'contacts') {
        setContactsOpen(true);
      } else if (detail?.menu === 'secrets') {
        setSecretsOpen(true);
      }
    };
    window.addEventListener('expand-sidebar-menu', handleExpandMenu);
    return () => window.removeEventListener('expand-sidebar-menu', handleExpandMenu);
  }, []);

  // Close hover submenu on clicking outside
  useEffect(() => {
    if (!activeHoverMenu) return;
    const handleOutsideClick = () => {
      setActiveHoverMenu(null);
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [activeHoverMenu]);

  // Use subscription status hook
  const subscriptionStatus = useSubscriptionStatus(user?.subscription, currentTime);
  const subscriptionLabel = !user?.subscription && user?.subscriptionAccess?.active
    ? 'Workspace subscription'
    : subscriptionStatus.displayPlanName;

  // Check if user is suspended
  const isSuspended = user?.suspended === true;

  const isBillingOwner = userSettingsData?.workspace?.isBillingOwner ?? true;
  const activeWorkspaceType = user?.workspaces?.find((workspace) => workspace.workspaceId === apiService.getWorkspaceId())?.workspaceType ?? 'live';

  // Filter navigation items (including suspension and special access checks)
  const filteredNavigation = navigation
    .filter(item => {
      // 1. Suspension check
      if (isSuspended && !item.suspendedAccess) return false;

      // The local use-case preference narrows visible navigation only; route and permission guards remain authoritative.
      if (emailUseCasePreference === 'transactional' && !TRANSACTIONAL_NAVIGATION.has(item.href)) return false;

      // 2. Billing owner only check
      if (item.ownerOnly && !isBillingOwner) return false;
      if (item.href === '/mailbox' && activeWorkspaceType === 'sandbox') return false;
      if (item.href === '/virtual-inbox' && activeWorkspaceType !== 'sandbox') return false;

      const requiredPermission = item.name === 'Secrets'
        ? ['transactional_api_key:read', 'marketing_api_key:read', 'web_api_key:read', 'smtp:read', 'shared_secret:read']
        : NAV_PERMISSIONS[item.href];
      if (requiredPermission && !(Array.isArray(requiredPermission)
        ? requiredPermission.some(permission => hasPermission(permission))
        : hasPermission(requiredPermission))) return false;

      // 3. Beta features check
      const betaFeatures: string[] = [];
      if (betaFeatures.includes(item.name)) {
        // Only show in development OR for the specific user janeevjoy@gmail.com
        return env === 'development' || user?.email === 'janeevjoy@gmail.com';
      }

      return true;
    })
    .map(item => {
      const permittedChildren = item.children?.filter(child => {
        if (emailUseCasePreference === 'marketing' && child.href === '/smtp') return false;
        const requiredPermission = NAV_PERMISSIONS[child.href];
        return !requiredPermission || (Array.isArray(requiredPermission)
          ? requiredPermission.some(permission => hasPermission(permission))
          : hasPermission(requiredPermission));
      }).map(child => child.badge === 'New' && !shouldShowNavNewBadge(child)
        ? { ...child, badge: undefined, badgeType: undefined }
        : child);
      if (item.badge === 'New' && !shouldShowNavNewBadge(item)) {
        return { ...item, children: permittedChildren, badge: undefined, badgeType: undefined };
      }
      return { ...item, children: permittedChildren };
    })
    .filter(item => !item.children || item.children.length > 0);

  const sidebarSections = [
    { name: 'Workspace', routes: ['/', '/analytics'] },
    { name: 'Email', routes: ['/domains', '/mailbox', '/virtual-inbox', '/templates'] },
    { name: 'Audience', routes: ['/contacts', '/contacts/suppressions'] },
    { name: 'Automation', routes: ['/campaigns', '/workflows'] },
    { name: 'Developers', routes: ['/api-keys', '/webhooks', '/integrations'] },
    { name: 'Account', routes: ['/pricing', '/account'] },
  ];
  const groupedNavigation = sidebarSections.flatMap(section => section.routes.flatMap(href => {
    const item = filteredNavigation.find(candidate => candidate.href === href);
    return item ? [{ ...item, group: section.name }] : [];
  }));

  // Check if current page is accessible for suspended users
  const isCurrentPageRestricted = isSuspended && !SUSPENDED_ALLOWED_PAGES.includes(location.pathname);

  return (
    <div className="flex flex-col h-screen bg-transparent transition-colors duration-200">
      {/* Maintenance Banner - full width at top */}
      <MaintenanceBanner />

      {/* User portal banners (notifications + surveys) - full width, below maintenance banner */}
      <UserPortalBanners />

      {userSettingsData?.emailStatus === 'inactive' && (
        <div role="alert" className="flex w-full items-center justify-center gap-1 bg-red-700 px-4 py-2 text-center text-sm font-medium text-white dark:bg-red-800">
          <span>Communication email is invalid. Update and verify your communication email under </span>
          <Link to="/account" className="font-semibold underline underline-offset-2 hover:text-red-100"><span>Accounts</span></Link>
          <span>.</span>
        </div>
      )}

      {/* Entra users without a mailbox must verify a separate communication address. */}
      <LegalAcceptanceGate />
      <CommunicationEmailPrompt />

      {reauthRequired && (
        <div role="alert" className="fixed inset-x-3 top-3 z-200 mx-auto flex max-w-3xl items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 shadow-lg dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <span>Your security settings changed and this session is no longer valid. Please sign in again.</span>
          <button type="button" onClick={() => void logout()} className="rounded-full bg-amber-700 px-3 py-1 font-medium text-white hover:bg-amber-800"><span>Sign in again</span></button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div id="app-sidebar" className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed top-0 h-dvh left-0 z-50 ${desktopSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'} w-64 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:h-auto lg:rounded-2xl lg:m-2 lg:overflow-hidden flex flex-col pb-2 lg:pb-0`}>
          {/* Logo and Close Button */}
          <div className={`flex items-center ${desktopSidebarCollapsed ? 'lg:justify-center' : 'lg:justify-between'} justify-between h-16 shrink-0 px-4 border-b border-zinc-200 dark:border-zinc-800`}>
            <Link to="https://www.simplysend.email" className="flex items-center gap-2 group">
              <span className="relative block h-7 w-7 shrink-0"><img src="/assets/images/sse-logo-bw-256x.png" alt="Simply Send" className="h-full w-full dark:hidden" /><img src="/assets/images/sse-logo-bw-256x-dark.png" alt="Simply Send" className="hidden h-full w-full dark:block" /></span>
              <span className={`font-semibold text-lg whitespace-nowrap text-zinc-900 dark:text-zinc-100 ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`} translate="no">
                Simply Send
              </span>
            </Link>
            <button
              aria-label="Close navigation"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-full hover:bg-white dark:hover:bg-zinc-800 transition-colors"
            >
              <X className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav aria-label="Main navigation" className="flex-1 min-h-0 overflow-y-auto px-2 py-3">
            <ul className="space-y-0.5">
              {groupedNavigation.map((item, index) => {
                const Icon = item.icon;
                const hasChildren = !!item.children;
                const isAnyChildActive = hasChildren
                  ? item.children!.some(child => location.pathname === child.href || (child.href !== '/contacts' && location.pathname.startsWith(child.href + '/')))
                  : false;
                const isActive = isAnyChildActive || location.pathname === item.href;
                const isMenuOpen = item.name === 'Contacts' ? contactsOpen : item.name === 'Secrets' ? secretsOpen : false;
                const toggleMenu = () => {
                  if (item.name === 'Contacts') setContactsOpen(!contactsOpen);
                  else if (item.name === 'Secrets') setSecretsOpen(!secretsOpen);
                  if (item.featureBadgeId) dismissBadge(item.featureBadgeId);
                };

                return (
                  <li
                    key={item.name}
                    className="space-y-0.5 relative"
                  >
                    {(index === 0 || groupedNavigation[index - 1].group !== item.group) && (
                      item.group === 'Account' ? (
                        <div className="mx-2 my-3 border-t border-zinc-200 dark:border-zinc-800" />
                      ) : (
                        <div className={`${index === 0 ? 'pt-1' : 'pt-4'} pb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500 ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>
                          <span>{item.group}</span>
                        </div>
                      )
                    )}
                    {item.divider && (
                      <div className="my-1.5 border-t border-zinc-200 dark:border-zinc-800 mx-2" />
                    )}
                    {item.href.startsWith('http') ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          if (item.featureBadgeId) dismissBadge(item.featureBadgeId);
                        }}
                        className={`group relative flex items-center ${desktopSidebarCollapsed ? 'lg:justify-center lg:px-0 px-3' : 'px-3'} min-h-9 py-2 text-sm font-medium rounded-md transition-colors duration-200 text-zinc-600 hover:bg-white hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100`}
                        title={desktopSidebarCollapsed ? item.name : undefined}
                      >
                        <div className={`relative shrink-0 ${desktopSidebarCollapsed ? 'lg:mr-0 mr-2' : 'mr-2'}`}>
                          <Icon
                            className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/60 dark:group-hover:text-zinc-100 transition-colors duration-200"
                          />
                          {desktopSidebarCollapsed && (
                            <ExternalLink className="absolute -top-1 -right-2.5 h-2.5 w-2.5 text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/40 dark:group-hover:text-zinc-100 transition-colors duration-200" />
                          )}
                          {desktopSidebarCollapsed && (item.badge || item.badge2) && (
                            <span className={`absolute ${item.href.startsWith('http') ? '-bottom-0.5' : '-top-0.5'} -right-0.5 w-1.5 h-1.5 rounded-full ring-[1.5px] ring-white dark:ring-black ${getDotBadgeClasses(item.badgeType || item.badge2Type)}`} />
                          )}
                        </div>
                        <span className={`whitespace-nowrap ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>{item.name}</span>
                        {item.badge && (
                          <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${item.badge === 'Beta' ? 'border border-cyan-600/30 text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-950/40' : getBadgeClasses(item.badgeType)} ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>
                            {item.badge}
                          </span>
                        )}
                        {item.badge2 && (
                          <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${getBadgeClasses(item.badge2Type)} ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>
                            {item.badge2}
                          </span>
                        )}
                        <ExternalLink className={`ml-auto h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/40 dark:group-hover:text-zinc-100 transition-colors duration-200 shrink-0 ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`} />
                      </a>
                    ) : hasChildren && (isMobile || !desktopSidebarCollapsed) ? (
                      <div>
                        <button
                          onClick={toggleMenu}
                          aria-expanded={isMenuOpen}
                          className={`group flex items-center w-full px-3 min-h-9 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${isAnyChildActive
                            ? 'text-cyan-700 dark:text-cyan-300 bg-zinc-50 dark:bg-zinc-800/80'
                            : 'text-zinc-600 hover:bg-white hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100'
                            }`}
                        >
                          <Icon
                            className={`mr-2 h-4 w-4 shrink-0 transition-colors duration-200 ${isAnyChildActive
                              ? 'text-cyan-700 dark:text-cyan-300'
                              : 'text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/60 dark:group-hover:text-zinc-100'
                              }`}
                          />
                          <span className="whitespace-nowrap">{item.name}</span>
                          {item.badge && (
                            <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${item.badge === 'Beta' ? 'border border-cyan-600/30 text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-950/40' : getBadgeClasses(item.badgeType)} ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>
                              {item.badge}
                            </span>
                          )}
                          {item.badge2 && (
                            <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${getBadgeClasses(item.badge2Type)} ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>
                              {item.badge2}
                            </span>
                          )}
                          <ChevronDown
                            className={`ml-auto h-4 w-4 text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/40 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''
                              }`}
                          />
                        </button>
                        {isMenuOpen && (
                          <ul className="mt-1 ml-[15.5px] space-y-0.5 border-l border-zinc-200 dark:border-zinc-800 pl-2">
                            {item.children!.map((child) => {
                              const ChildIcon = child.icon;
                              const isChildActive = location.pathname === child.href || (child.href !== '/contacts' && location.pathname.startsWith(child.href + '/'));
                              return (
                                <li key={child.name} className="relative">
                                  <span aria-hidden="true" className="absolute left-[-8.5px] -translate-x-1/2 top-4 h-1 w-1 rounded-full bg-zinc-300 dark:bg-zinc-700" />
                                  <Link
                                    id={`nav-link-${child.href.replace('/', '')}`}
                                    to={child.href}
                                    onClick={() => {
                                      if (child.featureBadgeId) dismissBadge(child.featureBadgeId);
                                      setSidebarOpen(false);
                                    }}
                                    className={`group flex items-center min-h-8 px-2 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${isChildActive
                                      ? 'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300 font-semibold'
                                      : 'text-slate-600 hover:bg-white hover:text-slate-950 dark:text-zinc-100/60 dark:hover:bg-zinc-800 dark:hover:text-zinc-100'
                                      }`}
                                  >
                                    <ChildIcon
                                      className={`mr-2 h-3.5 w-3.5 transition-colors duration-200 ${isChildActive
                                        ? 'text-cyan-700 dark:text-cyan-300'
                                        : 'text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/50 dark:group-hover:text-zinc-100'
                                        }`}
                                    />
                                    <span className="truncate">{child.name}</span>
                                    {child.badge && (
                                      <span className={`ml-2 px-1.5 py-0.5 text-[8px] font-semibold rounded whitespace-nowrap ${getBadgeClasses(child.badgeType)}`}>
                                        {child.badge}
                                      </span>
                                    )}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <Link
                        id={item.href.startsWith('http') ? undefined : `nav-link-${item.href.replace('/', '')}`}
                        to={item.href}
                        onMouseEnter={() => {
                          if (item.href === '/analytics') prefetchAnalyticsLanding();
                        }}
                        onFocus={() => {
                          if (item.href === '/analytics') prefetchAnalyticsLanding();
                        }}
                        onClick={(e) => {
                          if (desktopSidebarCollapsed && hasChildren) {
                            e.preventDefault();
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            if (activeHoverMenu?.name === item.name) {
                              setActiveHoverMenu(null);
                            } else {
                              setActiveHoverMenu({
                                name: item.name,
                                top: rect.top,
                                left: rect.right,
                                children: item.children || [],
                              });
                            }
                          } else {
                            setSidebarOpen(false);
                          }
                          if (item.featureBadgeId) dismissBadge(item.featureBadgeId);
                        }}
                        className={`group relative flex items-center ${desktopSidebarCollapsed ? 'lg:justify-center lg:px-0 px-3' : 'px-3'} min-h-9 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${isActive
                          ? 'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300'
                          : 'text-zinc-600 hover:bg-white hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100'
                          }`}
                        title={desktopSidebarCollapsed ? item.name : undefined}
                      >
                        <div className={`relative shrink-0 ${desktopSidebarCollapsed ? 'lg:mr-0 mr-2' : 'mr-2'}`}>
                          <Icon
                            className={`h-4 w-4 shrink-0 transition-colors duration-200 ${isActive
                              ? 'text-cyan-700 dark:text-cyan-300'
                              : 'text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/60 dark:group-hover:text-zinc-100'
                              }`}
                          />
                          {desktopSidebarCollapsed && (item.badge || item.badge2) && (
                            <span className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ring-[1.5px] ring-white dark:ring-black ${getDotBadgeClasses(item.badgeType || item.badge2Type)}`} />
                          )}
                          {desktopSidebarCollapsed && hasChildren && (
                            <ChevronRight
                              className={`absolute -bottom-1 -right-3 h-3 w-3 transition-colors duration-200 ${isActive
                                ? 'text-cyan-700 dark:text-cyan-300'
                                : 'text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/60 dark:group-hover:text-zinc-100'
                                }`}
                            />
                          )}
                        </div>
                        <span className={`whitespace-nowrap ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>{item.name}</span>
                        {item.badge && (
                          <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${item.badge === 'Beta' ? 'border border-cyan-600/30 text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-950/40' : getBadgeClasses(item.badgeType)} ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>
                            {item.badge}
                          </span>
                        )}
                        {item.badge2 && (
                          <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${getBadgeClasses(item.badge2Type)} ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`}>
                            {item.badge2}
                          </span>
                        )}
                        {isActive && <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-0.75 rounded-full bg-cyan-500" />}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="mt-auto shrink-0 border-t border-zinc-200 dark:border-zinc-800">
            <div className={`px-0 py-1.5 ${desktopSidebarCollapsed ? 'lg:px-1' : 'lg:pl-0 lg:pr-1.5'}`}>
              {!isSuspended && (
                <WorkspaceSwitcher
                  collapsed={desktopSidebarCollapsed && !isMobile}
                />
              )}
            </div>
            {/* User Profile */}
            {isSuspended ? (
              <div
                className={`block ${desktopSidebarCollapsed ? 'lg:py-2.5 lg:px-0 px-4 py-2.5 lg:text-center lg:flex lg:justify-center' : 'px-4 py-2.5'} border-t border-zinc-200 dark:border-zinc-800`}
                title={desktopSidebarCollapsed ? user?.settings?.profile?.name || 'Account' : undefined}
              >
                <div className="flex items-center">
                  <div className="relative shrink-0">
                    <div className="w-8 h-8 bg-linear-to-br from-slate-300 to-slate-400 rounded-full flex items-center justify-center">
                      <span className="text-zinc-100 text-xs font-medium">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                      {user?.settings?.profile?.pictureUrl && (
                        <img
                          src={user.settings.profile.pictureUrl}
                          alt=""
                          className="absolute inset-0 h-8 w-8 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                          onError={(event) => { event.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-slate-900"></div>
                  </div>
                  <div className={`ml-2 min-w-0 ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`} data-clarity-mask="true">
                    <p className="text-[13px] font-medium text-slate-900 dark:text-zinc-100 truncate">{user?.settings?.profile?.name}</p>
                    <div className="flex items-center">
                      <span className={`text-[11px] capitalize ${subscriptionStatus.isPending && !subscriptionStatus.isExpired
                        ? 'text-yellow-700 dark:text-yellow-400 animate-pulse'
                        : 'text-slate-500 dark:text-zinc-100/60'
                        }`}>
                        {subscriptionLabel}
                      </span>
                    </div>
                  </div>
                  {!isSidebarCollapsed && (
                    <span
                      className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-lg"
                      title={activePortalRegion.label}
                      aria-label={`${activePortalRegion.label} region`}
                      role="img"
                    >
                      {portalRegionFlags[activePortalRegion.id] || '🌐'}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <Link
                to="/account"
                onClick={() => setSidebarOpen(false)}
                className={`block ${desktopSidebarCollapsed ? 'lg:py-2.5 lg:px-0 px-4 py-2.5 lg:text-center lg:flex lg:justify-center' : 'px-4 py-2.5'} border-t border-zinc-200 dark:border-zinc-800 hover:bg-white dark:hover:bg-zinc-800 transition-colors cursor-pointer`}
                title={desktopSidebarCollapsed ? user?.settings?.profile?.name || 'Account' : undefined}
              >
                <div className="flex items-center">
                  <div className="relative shrink-0">
                    <div className="w-8 h-8 bg-linear-to-br from-slate-300 to-slate-400 rounded-full flex items-center justify-center">
                      <span className="text-zinc-100 text-xs font-medium">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                      {user?.settings?.profile?.pictureUrl && (
                        <img
                          src={user.settings.profile.pictureUrl}
                          alt=""
                          className="absolute inset-0 h-8 w-8 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                          onError={(event) => { event.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-slate-900"></div>
                  </div>
                  <div className={`ml-2 min-w-0 ${desktopSidebarCollapsed ? 'lg:hidden' : ''}`} data-clarity-mask="true">
                    <p className="text-[13px] font-medium text-slate-900 dark:text-zinc-100 group-hover:text-cyan-600 transition-colors truncate">{user?.settings?.profile?.name}</p>
                    <div className="flex items-center">
                      <span className={`text-[11px] capitalize ${subscriptionStatus.isPending && !subscriptionStatus.isExpired
                        ? 'text-yellow-700 dark:text-yellow-400 animate-pulse'
                        : 'text-slate-500 dark:text-zinc-100/60'
                        }`}>
                        {subscriptionLabel}
                      </span>
                    </div>
                  </div>
                  {!isSidebarCollapsed && (
                    <span
                      className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-lg"
                      title={activePortalRegion.label}
                      aria-label={`${activePortalRegion.label} region`}
                      role="img"
                    >
                      {portalRegionFlags[activePortalRegion.id] || '🌐'}
                    </span>
                  )}
                </div>
              </Link>
            )}

            <button
              onClick={logout}
              className={`flex items-center ${isSidebarCollapsed ? 'lg:justify-center lg:px-0 px-4' : 'justify-between px-4'} w-full py-2 text-[13px] font-medium text-slate-700 hover:bg-white hover:text-slate-900 dark:text-slate-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors duration-200`}
              title={isSidebarCollapsed ? "Sign Out" : undefined}
            >
              <span className={`flex items-center min-w-0 ${isSidebarCollapsed ? 'lg:justify-center' : ''}`}>
                <LogOut className={`${isSidebarCollapsed ? 'lg:mr-0 mr-2' : 'mr-2'} h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400`} />
                <span className={isSidebarCollapsed ? 'lg:hidden' : ''}>Sign Out</span>
              </span>
            </button>
          </div>
        </div>

        {/* Main content */}
        < div className="flex-1 flex flex-col overflow-hidden transition-colors duration-200" >
          {/* Header */}
          < header className="shrink-0" >
            <div className="flex items-center justify-between px-4 h-12">
              <div className="flex items-center">
                {/* Mobile menu button */}
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mr-3"
                  aria-label="Toggle mobile menu"
                >
                  {sidebarOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </button>
                {/* Desktop collapse button */}
                <button
                  onClick={toggleDesktopSidebar}
                  className="hidden lg:block text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mr-3"
                  aria-label="Toggle sidebar"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </div>

              <div className="flex items-center space-x-2">
                {/* Support / resources menu */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowInfoMenu(!showInfoMenu);
                      setShowDiscordMenu(false);
                    }}
                    className="relative inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 hover:text-cyan-600 dark:text-gray-400 dark:hover:text-cyan-400 focus:outline-none transition-colors rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    aria-label="Support"
                    title="Support"
                    aria-expanded={showInfoMenu}
                  >
                    <LifeBuoy className="h-3.5 w-3.5" />
                    <span>Support</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${showInfoMenu ? 'rotate-180' : ''}`} />
                    {visibleInfoMenuItems.some((item) => item.badge) && (
                      <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-cyan-600 dark:bg-cyan-400 ring-[1.5px] ring-white dark:ring-zinc-900" />
                    )}
                  </button>
                  {showInfoMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowInfoMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-52 rounded-lg shadow-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 z-50 py-1">
                        {hasPermission('support_page:create') && (
                          <>
                            <button
                              onClick={() => {
                                setShowSupportPage(true);
                                setShowInfoMenu(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
                            >
                              <Siren className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                              <span>Page Support</span>
                            </button>
                            <div className="my-1 border-t border-zinc-100 dark:border-zinc-700" />
                          </>
                        )}
                        {visibleInfoMenuItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <a
                              key={item.name}
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                if (item.featureBadgeId) dismissBadge(item.featureBadgeId);
                                setShowInfoMenu(false);
                              }}
                              className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 transition-colors"
                            >
                              <Icon className="h-3.5 w-3.5 shrink-0" />
                              <span>{item.name}</span>
                              {item.badge && (
                                <span className={`ml-auto px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap ${getBadgeClasses(item.badgeType)}`}>
                                  {item.badge}
                                </span>
                              )}
                              {!item.badge && (
                                <ExternalLink className="ml-auto h-3 w-3 text-zinc-400 shrink-0" />
                              )}
                            </a>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                {/* Discord menu */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowDiscordMenu(!showDiscordMenu);
                      setShowInfoMenu(false);
                    }}
                    className="p-1.5 text-gray-500 hover:text-[#5865F2] dark:text-gray-400 dark:hover:text-[#5865F2] focus:outline-none transition-colors"
                    aria-label="Discord"
                    title="Discord"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </button>
                  {showDiscordMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowDiscordMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 z-50 py-1">
                        <a
                          href="https://discord.com/channels/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setShowDiscordMenu(false)}
                          className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300 hover:text-[#5865F2] dark:hover:text-[#5865F2] hover:bg-zinc-200 dark:hover:bg-zinc-200 transition-colors"
                        >
                          <DiscordIcon className="h-3.5 w-3.5 text-[#5865F2]" />
                          <span>Discord Channel</span>
                        </a>
                        <a
                          href="https://discord.gg/6MyVyB9GTB"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setShowDiscordMenu(false)}
                          className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300 hover:text-[#5865F2] dark:hover:text-[#5865F2] hover:bg-zinc-200 dark:hover:bg-zinc-200 transition-colors"
                        >
                          <DiscordIcon className="h-3.5 w-3.5 text-[#5865F2]" />
                          <span>Discord Invitation</span>
                        </a>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setShowBugReport(true)}
                  className="p-1.5 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-500 focus:outline-none transition-colors"
                  aria-label="Report a bug"
                  title="Report a bug"
                >
                  <Bug className="h-4 w-4" />
                </button>
                <button
                  onClick={toggleTheme}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {theme === 'dark' ? (
                    <Sun className="h-4 w-4 text-white hover:text-yellow-400 transition-colors" />
                  ) : (
                    <Moon className="h-4 w-4 hover:text-blue-500 transition-colors" />
                  )}
                </button>
              </div>
            </div>
          </header >


          {/* Payment Failure Banner */}
          {subscriptionStatus.paymentFailed && !dismissedPaymentBanner && (
            <PaymentFailureBanner
              invoiceUrl={subscriptionStatus.failedInvoiceUrl}
              onDismiss={() => setDismissedPaymentBanner(true)}
            />
          )}

          {/* Email Sending Paused Banner */}
          {user?.pauseSending && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/50 px-4 py-3 transition-all duration-300">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-center relative">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-amber-600 dark:bg-amber-400 animate-pulse"></div>
                  <span className="font-semibold text-amber-900 dark:text-amber-200">Email Sending Paused</span>
                </div>
                <span className="hidden sm:inline text-amber-300 dark:text-amber-800">|</span>
                <span className="text-amber-700 dark:text-amber-300 truncate max-w-75 sm:max-w-md">
                  {user?.pauseSendingReason || 'Your email sending has been temporarily paused. Please contact support for assistance.'}
                </span>

                <div className="flex items-center gap-2 mt-2 sm:mt-0 sm:ml-4">
                  <a
                    href="https://www.simplysend.email/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium bg-white dark:bg-amber-900/40 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 px-3 py-1 rounded-full hover:bg-amber-50 dark:hover:bg-amber-900/60 transition-colors shadow-sm"
                  >
                    Contact Support
                  </a>
                  <button
                    onClick={() => setShowPauseDetails(!showPauseDetails)}
                    className="p-1 rounded-full hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-600 dark:text-amber-400 transition-colors"
                    title={showPauseDetails ? "Hide details" : "Show details"}
                  >
                    {showPauseDetails ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Suspension Banner */}
          {isSuspended && (
            <div className="bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-900/50 px-4 py-3 transition-all duration-300">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-center relative">
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="font-semibold text-red-900 dark:text-red-200">Account Suspended</span>
                </div>
                <span className="hidden sm:inline text-red-300 dark:text-red-800">|</span>
                <span className="text-red-700 dark:text-red-300 truncate max-w-75 sm:max-w-md">
                  {user?.suspensionReason ? `Reason: ${user.suspensionReason}` : 'Please contact support to restore access.'}
                </span>

                <div className="flex items-center gap-2 mt-2 sm:mt-0 sm:ml-4">
                  <a
                    href="https://www.simplysend.email/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium bg-white dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/60 transition-colors shadow-sm"
                  >
                    Contact Support
                  </a>
                  <button
                    onClick={() => setShowRestrictedView(!showRestrictedView)}
                    className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 transition-colors"
                    title={showRestrictedView ? "Hide details" : "Show details"}
                  >
                    {showRestrictedView ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Page content */}
          <main className="flex-1 overflow-auto pb-[25vh]">
            {isCurrentPageRestricted || showRestrictedView || showPauseDetails ? (
              <div className="flex flex-col items-center justify-center h-full p-8 animate-in fade-in duration-300">
                <div className="w-full max-w-md text-center">
                  {showPauseDetails && !showRestrictedView && !isCurrentPageRestricted ? (
                    <>
                      <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900/20 ring-1 ring-orange-100 dark:ring-orange-900/40">
                        <Send className="h-8 w-8 text-orange-500 dark:text-orange-400" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-3">Email Sending Paused</h2>
                      <div className="bg-orange-50/50 dark:bg-orange-900/10 rounded-xl p-4 mb-8 border border-orange-100 dark:border-orange-900/20">
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm whitespace-pre-wrap text-left">
                          {user?.pauseSendingReason || 'Your email sending has been temporarily paused. Please contact support for assistance.'}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <a
                          href="https://www.simplysend.email/contact"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 bg-orange-600 text-zinc-100 dark:text-zinc-100 rounded-full text-sm font-medium transition-all hover:bg-orange-700 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                        >
                          Contact Support
                        </a>
                        <button
                          onClick={() => setShowPauseDetails(false)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium transition-all hover:bg-gray-200 dark:hover:bg-zinc-700"
                        >
                          Hide Details
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20 ring-1 ring-red-100 dark:ring-red-900/40">
                        <ShieldX className="h-8 w-8 text-red-500 dark:text-red-400" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-3">Access Restricted</h2>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm mb-8 max-w-sm text-center mx-auto">
                        {isSuspended && user?.suspensionReason ? user.suspensionReason : 'Your account is currently suspended. Email sending via API Keys and SMTP credentials has been disabled.'}
                        {!user?.suspensionReason && (
                          <>
                            <br className="hidden sm:block" />
                            Please contact support to resolve the issue and restore full access.
                          </>
                        )}
                      </p>

                      <div className="flex flex-col items-center justify-center gap-3">
                        {isCurrentPageRestricted && (
                          <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center justify-center text-xs font-medium bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-zinc-700 px-3 py-1 rounded-full hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors shadow-sm"
                          >
                            Go Back
                          </button>
                        )}
                        <a
                          href="https://www.simplysend.email/contact"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center text-xs font-medium bg-zinc-900 dark:bg-zinc-100 text-zinc-100 dark:text-zinc-900 px-3 py-1 rounded-full hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors shadow-sm"
                        >
                          Contact Support
                        </a>
                        {!isCurrentPageRestricted && (
                          <button
                            onClick={() => setShowRestrictedView(false)}
                            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors mt-1"
                          >
                            Hide Details
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              children
            )}
          </main>
        </div >

        {/* Mobile sidebar overlay */}
        {
          sidebarOpen && (
            <div
              className="fixed inset-0 bg-white dark:bg-black z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )
        }

        {/* Global AI Assistant */}
        {canGenerateAiTemplates && <Suspense fallback={null}>
          <AiTemplateCreator
            isOpen={isOpen}
            isMinimized={isMinimized}
            onClose={closeAssistant}
            setIsMinimized={setIsMinimized}
            onTemplateCreated={onTemplateCreated || undefined}
          />
        </Suspense>}

        {/* Global Contacts Onboarding Tour */}
        <ContactsOnboardingTour />

        {/* Bug Report Popup */}
        <Suspense fallback={null}>
          <BugReportPopup
            isOpen={showBugReport}
            onClose={() => setShowBugReport(false)}
          />
          <BugReportPopup
            isOpen={showSupportPage}
            onClose={() => setShowSupportPage(false)}
            variant="supportPage"
          />
        </Suspense>

        {/* Hover Submenu for Collapsed Sidebar */}
        {
          activeHoverMenu && !isMobile && (
            <ModalPortal>
              <div
                className="fixed z-9999 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md border border-slate-200/80 dark:border-zinc-800/80 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.5)] py-1.5 min-w-50 pointer-events-auto transition-all duration-200 animate-in fade-in slide-in-from-left-2"
                style={{
                  top: `${activeHoverMenu.top}px`,
                  left: `${activeHoverMenu.left + 8}px`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 dark:text-zinc-500 border-b border-slate-100 dark:border-zinc-900/50 mb-1">
                  <span>{activeHoverMenu.name}</span>
                </div>
                <ul className="space-y-0.5 px-1.5">
                  {activeHoverMenu.children.map((child) => {
                    const ChildIcon = child.icon;
                    const isChildActive = location.pathname === child.href || (child.href !== '/contacts' && location.pathname.startsWith(child.href + '/'));
                    return (
                      <li key={child.name}>
                        <Link
                          id={`nav-link-collapsed-${child.href.replace('/', '')}`}
                          to={child.href}
                          onClick={() => {
                            if (child.featureBadgeId) dismissBadge(child.featureBadgeId);
                            setActiveHoverMenu(null);
                          }}
                          className={`group flex items-center px-3 py-2 text-[13px] font-medium rounded-lg transition-colors duration-200 ${isChildActive
                            ? 'bg-slate-100 text-slate-900 dark:bg-white/10 dark:text-zinc-100 font-semibold'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950 dark:text-zinc-100/60 dark:hover:bg-white/5 dark:hover:text-zinc-100'
                            }`}
                        >
                          <ChildIcon
                            className={`mr-2 h-4 w-4 transition-colors duration-200 ${isChildActive
                              ? 'text-slate-900 dark:text-zinc-100'
                              : 'text-slate-400 group-hover:text-slate-600 dark:text-zinc-100/50 dark:group-hover:text-zinc-100'
                              }`}
                          />
                          <span className="truncate">{child.name}</span>
                          {child.badge && (
                            <span className={`ml-2 px-1.5 py-0.5 text-[8px] font-semibold rounded whitespace-nowrap ${getBadgeClasses(child.badgeType)}`}>
                              {child.badge}
                            </span>
                          )}
                          <span className={`${isChildActive ? 'bg-cyan-500' : 'bg-transparent'} ml-auto inline-block w-1.5 h-1.5 rounded-full shrink-0`} />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </ModalPortal>
          )
        }
      </div >

      <Suspense fallback={null}>
        <CreateWorkspaceModal
          isOpen={showCreateWorkspaceModal}
          onClose={() => setShowCreateWorkspaceModal(false)}
          onCreated={(wsId) => {
            setShowCreateWorkspaceModal(false);
            void switchWorkspace(wsId).catch(error => {
              toast.error(error instanceof Error ? error.message : 'Failed to switch workspace. Please try again.');
            });
          }}
          availableWorkspaces={user?.workspaces}
        />
      </Suspense>
    </div >
  );
}
